import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';
import { store } from '../store';
import { logout } from '../store/slices/authSlice';

/**
 * Get the API base URL based on environment configuration
 * This is reused across the app for consistent URL handling
 */
export const getBaseURL = (): string => {
  // Check for environment variable first
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  
  // Development vs Production fallback
  if (import.meta.env.DEV) {
    // Development: Point to backend server
    return 'http://localhost:8000/api/v1';
  } else {
    // Production: Use your deployed backend URL
    // TODO: Replace with your actual backend URL
    return 'https://your-backend-domain.com/api/v1';
  }
};

class BaseAPIService {
  protected api: AxiosInstance;

  constructor(baseURL?: string) {
    this.api = axios.create({
      baseURL: baseURL || getBaseURL(),
      timeout: 50000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = store.getState().auth.token || localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        // Handle authentication errors
        if (error.response?.status === 401) {
          store.dispatch(logout());
          window.location.href = '/login';
        }
        
        // Handle emergency blocks - auto logout when user access is blocked
        const emergencyErrorCodes = ['API_BLOCKED', 'USER_BLOCKED', 'NON_ADMIN_BLOCKED', 'EMERGENCY_ACTIVE'];
        const isEmergencyBlock = (
          (error.response?.status === 503 || error.response?.status === 403) &&
          emergencyErrorCodes.includes(error.response?.data?.error_code)
        );
        
        // Also handle maintenance mode (might return HTML instead of JSON)
        const isMaintenanceMode = (
          error.response?.status === 503 &&
          error.response?.headers?.['content-type']?.includes('text/html')
        );
        
        if (isEmergencyBlock) {
          const errorCode = error.response.data.error_code as string;
          console.warn(`Emergency system blocked user access: ${errorCode} - logging out user`);
          
          // Show emergency notification with appropriate message
          const emergencyMessages: Record<string, string> = {
            'API_BLOCKED': 'All API access is temporarily blocked due to emergency mode',
            'USER_BLOCKED': 'Your account has been temporarily restricted',
            'NON_ADMIN_BLOCKED': 'Only admin users can access the system during emergency mode',
            'EMERGENCY_ACTIVE': 'System is in emergency mode - access restricted'
          };
          
          const emergencyMessage = error.response.data.message || 
                                 emergencyMessages[errorCode] || 
                                 'Access temporarily restricted due to emergency mode';
          
          toast.error(emergencyMessage, {
            duration: 6000,
            position: 'top-center',
            style: {
              background: '#ef4444',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              maxWidth: '500px'
            }
          });
          
          // Logout user and redirect
          store.dispatch(logout());
          
          // Small delay to let the toast show before redirect
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
        }
        
        // Handle maintenance mode
        if (isMaintenanceMode) {
          console.warn('System in maintenance mode - logging out user');
          
          toast.error('System is currently under maintenance. Please try again later.', {
            duration: 6000,
            position: 'top-center',
            style: {
              background: '#f59e0b',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '14px',
              maxWidth: '500px'
            }
          });
          
          // Logout user and redirect
          store.dispatch(logout());
          
          // Small delay to let the toast show before redirect
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
        }
        
        return Promise.reject(error);
      }
    );
  }

  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.get<T>(url, config);
    return response.data;
  }

  protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, config);
    return response.data;
  }

  protected async postFormData<T>(url: string, data: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  protected async postFormUrlEncoded<T>(url: string, data: URLSearchParams, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.post<T>(url, data, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.put<T>(url, data, config);
    return response.data;
  }

  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.delete<T>(url, config);
    return response.data;
  }

  protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.api.patch<T>(url, data, config);
    return response.data;
  }
}

export default BaseAPIService; 